import { GameObj, KAPLAYCtx, OpacityComp, PosComp } from "kaplay";
import { BARRIER_HEIGHT, FLOOR_Y, LASER_BOTTOM_Y, PLAYER_START_Y, SCENES, SHAFT_CENTER_X, SHAFT_WIDTH } from "../../constants";
import {
    DEATH_TRANSITION_DELAY_SEC,
    FIRST_SPAWN_DELAY_SEC,
    MAX_COMBO,
    POINTS_PER_PASS,
    SURVIVAL_POINTS_PER_SEC,
} from "../../config";
import { ColorController, COLORS } from "../../colors";
import { barrierSpeedFor, pickBarrierColor, spawnIntervalFor } from "../../rules/difficulty";
import { BarrierBody, isTouchingLaser, overlapsBarrier, stepPlayer } from "../../rules/physics";
import { getHighScore } from "../../storage";
import { PALETTE, textStyle } from "../../theme";
import { GameColor, GameOverStats, PrimaryColor } from "../../types";
import { createColorButtons } from "./controls";
import { playVaporizeEffect, showFloatingText } from "./effects";
import { addLaser, addShaft } from "./environment";
import { createHud } from "./hud";
import { createPlayerCube } from "./player";

interface Barrier extends BarrierBody {
    obj: GameObj<PosComp | OpacityComp>;
}

const PASSED_BARRIER_OPACITY = 0.4;

export function registerGameScene(k: KAPLAYCtx): void {
    k.scene(SCENES.game, () => {
        const colorController = new ColorController("red");

        let score = 0;
        let combo = 1;
        let maxCombo = 1;
        let barriersPassed = 0;
        let isGameOver = false;
        let spawnTimer = FIRST_SPAWN_DELAY_SEC;
        let lastBarrierColor: GameColor = "red";
        let playerY = PLAYER_START_Y;
        let playerVelocityY = 0;
        const barriers: Barrier[] = [];

        addShaft(k);
        addLaser(k);
        const hud = createHud(k, getHighScore());
        const player = createPlayerCube(k, playerY, colorController.getActiveColor());
        const controls = createColorButtons(k, pressColor);

        function pressColor(color: PrimaryColor) {
            if (isGameOver) return;
            const newColor = colorController.press(color);
            player.showColor(newColor);
            // hud.setColor(newColor);
            controls.animatePress(color);
        }

        function addSurvivalScore(dt: number) {
            score += SURVIVAL_POINTS_PER_SEC * dt;
            hud.setScore(score);
        }

        function spawnBarrierWhenDue(dt: number) {
            spawnTimer -= dt;
            if (spawnTimer > 0) return;
            spawnTimer = spawnIntervalFor(score);

            const color = pickBarrierColor(score, lastBarrierColor, k.choose);
            lastBarrierColor = color;
            const topY = FLOOR_Y - BARRIER_HEIGHT;

            const obj = k.add([
                k.rect(SHAFT_WIDTH, BARRIER_HEIGHT, { radius: 5 }),
                k.pos(SHAFT_CENTER_X, topY + BARRIER_HEIGHT / 2),
                k.anchor("center"),
                k.color(...COLORS[color].rgb),
                k.outline(2, k.rgb(...PALETTE.white)),
                k.opacity(0.95),
                k.z(10),
            ]);

            // Child label moves and despawns with the barrier
            obj.add([
                k.text(COLORS[color].name, textStyle(12)),
                k.anchor("center"),
                k.color(20, 20, 30),
                k.z(11),
            ]);

            barriers.push({ obj, color, topY, isPassedThrough: false });
        }

        function moveBarriers(dt: number) {
            const speed = barrierSpeedFor(score);
            for (let i = barriers.length - 1; i >= 0; i--) {
                const barrier = barriers[i];
                barrier.topY -= speed * dt;
                // Reassign pos (not mutate) so KAPLAY picks up the transform change
                barrier.obj.pos = k.vec2(SHAFT_CENTER_X, barrier.topY + BARRIER_HEIGHT / 2);

                if (barrier.topY <= LASER_BOTTOM_Y) {
                    k.destroy(barrier.obj);
                    barriers.splice(i, 1);
                }
            }
        }

        function scoreMatchedPassThroughs() {
            const activeColor = colorController.getActiveColor();
            for (const barrier of barriers) {
                if (barrier.isPassedThrough || barrier.color !== activeColor) continue;
                if (!overlapsBarrier(playerY, barrier.topY)) continue;

                barrier.isPassedThrough = true;
                barrier.obj.opacity = PASSED_BARRIER_OPACITY;
                barriersPassed++;
                combo = Math.min(combo + 1, MAX_COMBO);
                maxCombo = Math.max(maxCombo, combo);

                const points = POINTS_PER_PASS * combo;
                score += points;
                hud.setCombo(combo);
                // Parentheses, not square brackets: KAPLAY text treats [..] as style tags
                showFloatingText(k, `+${points} (x${combo})`, SHAFT_CENTER_X, barrier.topY - 10, COLORS[barrier.color].rgb);
            }
        }

        function movePlayer(dt: number) {
            const step = stepPlayer(
                { centerY: playerY, velocityY: playerVelocityY },
                barriers,
                colorController.getActiveColor(),
                dt,
            );
            playerY = step.centerY;
            playerVelocityY = step.velocityY;
            player.moveTo(playerY);

            // Being carried by a barrier breaks the streak
            if (step.isRidingBarrier && combo > 1) {
                combo = 1;
                hud.setCombo(combo);
            }
        }

        function easePressedVisualsBack(dt: number) {
            controls.easeBack(dt);
            player.easeBack(dt);
            hud.easeBack(dt);
        }

        function triggerDeath() {
            isGameOver = true;
            player.hide();
            playVaporizeEffect(k, SHAFT_CENTER_X, playerY, COLORS[colorController.getActiveColor()].rgb);

            const stats: GameOverStats = { score: Math.floor(score), barriersPassed, maxCombo };
            k.wait(DEATH_TRANSITION_DELAY_SEC, () => k.go(SCENES.gameOver, stats));
        }

        k.onUpdate(() => {
            if (isGameOver) return;
            const dt = k.dt();

            addSurvivalScore(dt);
            spawnBarrierWhenDue(dt);
            moveBarriers(dt);
            scoreMatchedPassThroughs();
            movePlayer(dt);
            easePressedVisualsBack(dt);

            if (isTouchingLaser(playerY)) triggerDeath();
        });
    });

}
